import { AdvancedReportService, DetailRequest, FilterBuilder, FilterQueryMode, Status, translateCommonErrorStatus } from "@core/data";
import { ADVANCED_REPORT_OPTION_DEFAULT, AdvancedReportOption, AdvancedReportTemplate, AdvancedReportTemplateData, AnyReportComponentData, BaseReportComponent, clampComponentGeometry, ComponentTemplateData, GridDensity, ImageComponentData, NewPageOption, NewReportTemplate, PAGE_OPTION_DEFAULT, PageTemplateData, ReportComponentType, ReportDataContext, ReportOptionConfig, ReportPage, ReportTemplateTypes, resolveGridDensity, scaleComponentGeometry, scaleGridOptions } from "@core/data/types/advanced-report";
import { Action, createSelector, Selector, State, StateContext, Store } from "@ngxs/store";
import { Injectable } from "@angular/core";
import { AddReportPage, ChangeReportGridDensity, InitReportGenerator, LoadReportSampleKeys, LoadReportGenerator, AddReportPageOptions, ToggleEditMode, ExtractReportTemplate, LoadReportTemplateOptions, ApplyReportTemplate, EditReportOptions, UpdateReportTitle, RemoveReportPage, SetReportFilter } from "./advanced-report.state-actions";
import { switchMap, tap } from "rxjs/operators";
import { AdvancedReportModel, AdvancedReportStateModel, REPORT_STATE_TOKEN } from "./advanced-report.state-models";
import { ReportPageService } from "@core/data/api/reports/report-page.service";
import { ReportPageOptionService } from "@core/data/api/reports/report-page-option.service";
import { PageState } from "../../report-page/report-page-state/report-page.state";
import { PageModel } from "../../report-page/report-page-state/report-page.state-models";
import { ComponentModel } from "../../report-components/report-component.state-models";
import { ComponentState } from "../../report-components/report-component.state";
import { AdvancedReportTemplateService } from "@core/data/api/reports/advanced-report-template.service";
import { AdvancedReportCompilerService } from "@core/data/api/reports/advanced-report-compiler.service";
import { forkJoin, of } from "rxjs";
import { AdvancedReportOptionService } from "@core/data/api/reports/advanced-report-option.service";
import { ApplyReportPageOrientation, SetPageOptions } from "../../report-page/report-page-state/report-page.state-actions";
import { ReportComponentService } from "@core/data/api/reports/report-component.service";
import { Snackbar } from "@core/material";

const DEFAULTS: AdvancedReportStateModel = {
    reportKey: null,
    shareKey: null,
    ownerOrgKey: null,
    sampleKeys: [],
    editMode: false,
    entities: {}
}

const REPORT_DEFAULTS: AdvancedReportModel = {
    key: null,
    ownerOrgKey: null,

    pageKeys: [],

    report: null,
    templateOptions: [],

    status: Status.UNINITIALIZED,
    rightDrawerStatus: Status.UNINITIALIZED,
}

@State<AdvancedReportStateModel>({
    name: REPORT_STATE_TOKEN,
    defaults: DEFAULTS,
})
@Injectable()
export class AdvancedReportState {

    @Selector()
    static editMode(state: AdvancedReportStateModel) {
        return state.editMode;
    }

    @Selector()
    static getEntities(state: AdvancedReportStateModel) {
        return state.entities;
    }

    static getReport(key: string) {
        return createSelector(
            [AdvancedReportState.getEntities],
            (entities) => entities[key]
        );
    }

    constructor(
        private _store: Store,
        private _reportService: AdvancedReportService,
        private _reportOptionService: AdvancedReportOptionService,
        private _pageService: ReportPageService,
        private _pageOptionService: ReportPageOptionService,
        private _templateService: AdvancedReportTemplateService,
        private _compilerService: AdvancedReportCompilerService,
        private _componentService: ReportComponentService,
        private _snackbar: Snackbar,
    ) { }

    @Action(InitReportGenerator)
    initReportGenerator(ctx: StateContext<AdvancedReportStateModel>, action: InitReportGenerator) {

        ctx.setState({
            ...DEFAULTS,
            reportKey: action.reportKey,
            shareKey: action.shareKey,
            ownerOrgKey: action.orgKey,
            editMode: action.editMode,
            entities: {
                [action.reportKey]: {
                    ...REPORT_DEFAULTS,
                    key: action.reportKey,
                    ownerOrgKey: action.orgKey,
                    status: Status.LOADING,
                    rightDrawerStatus: Status.LOADING,
                }
            }
        });

        return ctx.dispatch(new LoadReportGenerator);
    }

    @Action(LoadReportGenerator)
    loadReportGenerator(ctx: StateContext<AdvancedReportStateModel>, action: LoadReportGenerator) {

        let state = ctx.getState();

        const detailRequest: DetailRequest = {
            related: [
                'options',
                'pages'
            ],
        };

        ctx.patchState({
            entities: {
                ...state.entities,
                [state.reportKey]: {
                    ...state.entities[state.reportKey],
                    status: Status.LOADING,
                }
            }
        })

        state = ctx.getState();

        const shareKey = state.shareKey;

        return this._reportService.get(state.reportKey, detailRequest, shareKey)
            .pipe(
                tap(
                    result => {
                        let pageKeys = result.pages.map(page => page.key);
                        ctx.patchState({
                            entities: {
                                ...state.entities,
                                [result.key]: {
                                    ...state.entities[state.reportKey],
                                    report: result,
                                    status: pageKeys.length > 0 ? Status.OK : Status.LOADING,
                                    pageKeys,
                                }
                            }
                        });

                        //? CACHE REPORT SAMPLE KEYS
                        forkJoin([
                            ctx.dispatch(LoadReportTemplateOptions),
                            ctx.dispatch(LoadReportSampleKeys),
                        ]);

                        if (pageKeys.length == 0) ctx.dispatch(new AddReportPage);

                    },
                    error => {
                        ctx.patchState({
                            entities: {
                                ...state.entities,
                                [state.reportKey]: {
                                    ...state.entities[state.reportKey],
                                    report: null,
                                    key: state.reportKey,
                                    ownerOrgKey: state.ownerOrgKey,
                                    status: translateCommonErrorStatus(error),
                                }
                            }
                        });
                    }
                )
            )
    }

    @Action(UpdateReportTitle)
    updateReportTitle(ctx: StateContext<AdvancedReportStateModel>, action: UpdateReportTitle) {
        let state = ctx.getState();

        let model: AdvancedReportModel = {
            ...state.entities[state.reportKey],
            report: {
                ...state.entities[state.reportKey].report,
                label: action.title,
            }
        };

        ctx.patchState({
            ...state,
            entities: {
                ...state.entities,
                [model.key]: {
                    ...state.entities[model.key],
                    report: {
                        ...model.report
                    }
                }
            }
        });

        return this._reportService.update(model.key, model.report);
    }

    // Local-only patch: the filter dialog already persisted the new filter itself, so this just
    // keeps the client's copy in sync without a network call or a full report reload.
    @Action(SetReportFilter)
    setReportFilter(ctx: StateContext<AdvancedReportStateModel>, action: SetReportFilter) {
        let state = ctx.getState();
        let model = state.entities[action.reportKey];
        if (!model) return;

        ctx.patchState({
            entities: {
                ...state.entities,
                [action.reportKey]: {
                    ...model,
                    report: {
                        ...model.report,
                        filter: action.filter,
                    }
                }
            }
        });
    }

    @Action(EditReportOptions)
    editReportOptions(ctx: StateContext<AdvancedReportStateModel>, action: EditReportOptions) {
        let state = ctx.getState();

        if (!editModeCheck(state.editMode)) return;

        const model: AdvancedReportModel = state.entities[action.reportKey];

        let updateOrientation: boolean = model.report.options?.config?.pageOrientation != action.config.pageOrientation;

        const config: ReportOptionConfig = {
            ...ADVANCED_REPORT_OPTION_DEFAULT,
            ...model.report.options?.config,
            ...action.config,
        };

        const options: AdvancedReportOption = {
            ...model.report.options,
            config: config,
        }

        ctx.patchState({
            entities: {
                ...state.entities,
                [action.reportKey]: {
                    ...model,
                    report: {
                        ...model.report,
                        options: {
                            ...options,
                        }
                    },
                    status: Status.LOADING,
                }
            }
        });

        if (updateOrientation) ctx.dispatch(new ApplyReportPageOrientation(state.entities[action.reportKey].pageKeys, config.pageOrientation));

        return this._reportOptionService.update(action.key, options)
            .pipe(
                tap(
                    result => {
                        state = ctx.getState();

                        ctx.patchState({
                            entities: {
                                ...state.entities,
                                [action.reportKey]: {
                                    ...state.entities[action.reportKey],
                                    status: Status.OK,
                                }
                            }
                        });
                    },
                    error => {
                        state = ctx.getState();

                        ctx.patchState({
                            entities: {
                                ...state.entities,
                                [action.reportKey]: {
                                    ...state.entities[action.reportKey],
                                    status: translateCommonErrorStatus(error),
                                }
                            }
                        });
                    }
                )
            )
    }

    @Action(ChangeReportGridDensity)
    changeReportGridDensity(ctx: StateContext<AdvancedReportStateModel>, action: ChangeReportGridDensity) {
        const state = ctx.getState();

        if (!editModeCheck(state.editMode)) return;

        const model: AdvancedReportModel = state.entities[action.reportKey];

        const oldDensity: GridDensity = resolveGridDensity(model.report.options?.config);
        const newDensity: GridDensity = action.density;

        if (oldDensity === newDensity) return;

        const ratio = newDensity / oldDensity;

        const components: { component: BaseReportComponent, pageModel: PageModel }[] = [];
        model.pageKeys.forEach((pageKey: string) => {
            const pageModel: PageModel = this._store.selectSnapshot(PageState.getPage(pageKey));
            if (!pageModel) return;

            pageModel.componentKeys.forEach((componentKey: string) => {
                const componentModel: ComponentModel = this._store.selectSnapshot(ComponentState.getComponent(componentKey));
                if (componentModel?.component) components.push({ component: componentModel.component, pageModel });
            });
        });

        ctx.patchState({
            entities: {
                ...state.entities,
                [action.reportKey]: {
                    ...model,
                    status: Status.LOADING,
                }
            }
        });

        const config: ReportOptionConfig = {
            ...ADVANCED_REPORT_OPTION_DEFAULT,
            ...model.report.options?.config,
            gridDensity: newDensity,
        };

        const options: AdvancedReportOption = {
            ...model.report.options,
            config: config,
        };

        // Rescale every component first; only flip the report density once all writes
        // succeed, so a partial failure can't leave a mixed-scale (corrupt) layout.
        // The rescale alone doesn't know about the page's own orientation, so we also clamp
        // against that page's actual bounds at the new density (mirrors the merge order
        // report-page.view.ts uses at render time) to guard against orientation/density combinations
        // that would otherwise leave a component off-page.
        const geometryWrites = components.map(({ component, pageModel }) => {
            const scaled = scaleComponentGeometry(component, ratio);

            const grid = scaleGridOptions({
                ...PAGE_OPTION_DEFAULT.gridOptions,
                ...pageModel.page.options?.config?.gridOptions,
            }, newDensity);

            const clamped = clampComponentGeometry(scaled, grid.maxCols, grid.maxRows);

            const payload: BaseReportComponent = JSON.parse(JSON.stringify(component));
            delete payload.options;

            payload.x = clamped.x;
            payload.y = clamped.y;
            payload.width = clamped.width;
            payload.height = clamped.height;

            return this._componentService.update(component.key, payload);
        });

        const writes$ = geometryWrites.length > 0 ? forkJoin(geometryWrites) : of(null);

        return writes$.pipe(
            switchMap(() => this._reportOptionService.update(action.key, options)),
            tap(
                () => {
                    this._snackbar.info(newDensity > oldDensity ? 'Switched to the denser grid.' : 'Switched to the standard grid.');

                    // Full re-init so pages/components re-render fresh — re-deriving each grid at
                    // the new density and reloading the rescaled component geometry from the server.
                    ctx.dispatch(new InitReportGenerator(state.ownerOrgKey, state.reportKey, state.editMode, state.shareKey));
                },
                error => {
                    const current = ctx.getState();

                    this._snackbar.error('Failed to change grid density. Your report was not changed — please reload and try again.');

                    ctx.patchState({
                        entities: {
                            ...current.entities,
                            [action.reportKey]: {
                                ...current.entities[action.reportKey],
                                status: Status.OK,
                            }
                        }
                    });
                }
            )
        );
    }

    @Action(AddReportPage)
    addReportPage(ctx: StateContext<AdvancedReportStateModel>, action: AddReportPage) {
        let state = ctx.getState();

        const reportKey = state.reportKey;

        let page: Partial<ReportPage> = {
            reportKey: state.reportKey,
            filter: null,
            index: state.entities[reportKey].pageKeys.length,
        };

        if (!editModeCheck(state.editMode)) return;

        return this._pageService.create(page)
            .pipe(
                tap(
                    result => {
                        state = ctx.getState();

                        let pageKeys = state.entities[state.reportKey].pageKeys;

                        pageKeys = [...pageKeys, result.key]

                        ctx.patchState({
                            entities: {
                                ...state.entities,
                                [state.reportKey]: {
                                    ...state.entities[state.reportKey],
                                }
                            }
                        })

                        // TODO: CHECK HOW PAGES SHOULD BE LOADED AFTER BEING ADDED
                        ctx.dispatch(new AddReportPageOptions(result.key, pageKeys));
                    },
                    error => {
                        ctx.patchState({
                            entities: {
                                ...state.entities,
                                [state.reportKey]: {
                                    ...state.entities[state.reportKey],
                                    status: translateCommonErrorStatus(error),
                                }
                            }
                        });
                    }
                )
            );
    }

    @Action(RemoveReportPage)
    removeReportPage(ctx: StateContext<AdvancedReportStateModel>, action: RemoveReportPage) {
        // TODO refactor if pages are being removed in another way than templates

        return this._pageService.delete(action.key);
    }

    @Action(AddReportPageOptions)
    addReportPageOptions(ctx: StateContext<AdvancedReportStateModel>, action: AddReportPageOptions) {
        let state = ctx.getState();

        const model = state.entities[state.reportKey];

        let options: NewPageOption = {
            pageKey: action.pageKey,
            config: {
                ...PAGE_OPTION_DEFAULT,
                pageOrientation: model.report?.options?.config?.pageOrientation || "portrait",
            },
        };

        if (!editModeCheck(state.editMode)) return;

        return this._pageOptionService.create(options)
            .pipe(
                tap(
                    result => {
                        ctx.patchState({
                            entities: {
                                ...state.entities,
                                [state.reportKey]: {
                                    ...state.entities[state.reportKey],
                                    status: Status.OK,
                                    pageKeys: [...action.pageKeys],
                                }
                            }
                        });

                        // The options created above (`result`) live only in this response — PageState
                        // still has page.options as null for this page. Patch it in directly (no
                        // re-fetch): a re-fetch here could race back into this same fallback if the
                        // GET doesn't yet see the just-created row, looping create → fetch → create.
                        ctx.dispatch(new SetPageOptions(action.pageKey, result));
                    },
                    error => {
                        ctx.patchState({
                            entities: {
                                ...state.entities,
                                [state.reportKey]: {
                                    ...state.entities[state.reportKey],
                                    status: translateCommonErrorStatus(error),
                                }
                            }
                        });
                    }
                )
            );
    }

    @Action(LoadReportSampleKeys)
    loadReportData(ctx: StateContext<AdvancedReportStateModel>, action: LoadReportSampleKeys) {
        let state = ctx.getState();

        return this._reportService.compile(state.reportKey, state.ownerOrgKey, state.shareKey)
            .pipe(
                tap(
                    result => {
                        state = ctx.getState();

                        ctx.patchState({
                            ...state,
                            sampleKeys: result?.samples || [],
                            entities: {
                                ...state.entities,
                            }
                        });
                    },
                    error => {
                        ctx.patchState({
                            sampleKeys: [],
                            entities: {
                                ...state.entities,
                                [state.reportKey]: {
                                    ...state.entities[state.reportKey],
                                    report: null,
                                    key: state.reportKey,
                                    ownerOrgKey: state.ownerOrgKey,
                                    status: translateCommonErrorStatus(error),
                                }
                            }
                        });
                    }
                )
            )
    }

    @Action(ToggleEditMode)
    toggleEditMode(ctx: StateContext<AdvancedReportStateModel>, action: ToggleEditMode) {
        return ctx.patchState({
            editMode: action.editMode,
        });
    }

    @Action(LoadReportTemplateOptions)
    loadReportTemplateOptions(ctx: StateContext<AdvancedReportStateModel>, action: LoadReportTemplateOptions) {
        let state = ctx.getState();

        if (state.shareKey) return;

        ctx.patchState({
            entities: {
                ...state.entities,
                [state.reportKey]: {
                    ...state.entities[state.reportKey],
                    rightDrawerStatus: Status.LOADING,
                }
            }
        });

        const fb = new FilterBuilder();

        fb.setQuery('type', action.types, FilterQueryMode.IN);

        return this._compilerService.templateOptions(state.reportKey, state.ownerOrgKey, fb.get())
            .pipe(
                tap(
                    result => {
                        state = ctx.getState();

                        ctx.patchState({
                            entities: {
                                ...state.entities,
                                [state.reportKey]: {
                                    ...state.entities[state.reportKey],
                                    rightDrawerStatus: Status.OK,
                                    templateOptions: result.templates,
                                }
                            }
                        });
                    },
                    error => {
                        console.error('Template Load Error', error);

                        ctx.patchState({
                            entities: {
                                ...state.entities,
                                [state.reportKey]: {
                                    ...state.entities[state.reportKey],
                                    rightDrawerStatus: Status.OK,
                                    templateOptions: [],
                                }
                            }
                        });
                    }
                )
            )
    }

    @Action(ApplyReportTemplate)
    applyReportTemplate(ctx: StateContext<AdvancedReportStateModel>, action: ApplyReportTemplate) {
        let state = ctx.getState();

        ctx.patchState({
            entities: {
                ...state.entities,
                [state.reportKey]: {
                    ...state.entities[state.reportKey],
                    status: Status.LOADING,
                }
            }
        });

        let model = state.entities[state.reportKey];

        model.pageKeys.forEach(key => {
            return ctx.dispatch(new RemoveReportPage(key));
        })

        return this._templateService.apply(action.template.key, state.reportKey)
            .pipe(
                tap(
                    result => {
                        ctx.dispatch(new LoadReportGenerator);
                    },
                    error => {
                        console.error('error', error)
                    }
                )
            )
    }

    @Action(ExtractReportTemplate)
    extractReportTemplate(ctx: StateContext<AdvancedReportStateModel>, action: ExtractReportTemplate) {

        const state = ctx.getState();

        const model: AdvancedReportModel = state.entities[state.reportKey];

        let templateData: AdvancedReportTemplateData = {
            config: model.report.options.config,
            pages: []
        };

        model.pageKeys.forEach((pageKey: string) => {
            let pageModel: PageModel = this._store.selectSnapshot(PageState.getPage(pageKey));

            const { page } = pageModel;

            let pageData: PageTemplateData = {
                index: page.index,
                config: page.options.config,
                components: [],
            };

            pageModel.componentKeys.forEach((componentKey: string) => {
                let componentModel: ComponentModel = this._store.selectSnapshot(ComponentState.getComponent(componentKey));

                const { component } = componentModel;

                let componentData: ComponentTemplateData = {
                    x: component.x,
                    y: component.y,
                    width: component.width,
                    height: component.height,
                    config: component.options.config,
                    type: component.type,
                    data: this.processComponentForTemplate(component.options.data, component.type),
                } as ComponentTemplateData;

                pageData.components.push(componentData);
            });

            templateData.pages.push(pageData);
        });

        const template: NewReportTemplate<AdvancedReportTemplate> = {
            ownerOrgKey: model.ownerOrgKey,
            label: `${model.report.label} Template`,
            description: null,
            isGlobal: false,
            type: ReportTemplateTypes.REPORT,
            data: templateData,
        };

        // TODO how should template results be handled
        return this._templateService.create(template)
            .pipe(
                tap(
                    result => {
                        ctx.dispatch(new LoadReportTemplateOptions);
                    },
                    error => {
                        console.error('error', error)
                    }
                )
            );
    }

    private processComponentForTemplate(data: AnyReportComponentData, type: ReportComponentType) {
        let dataCopy = JSON.parse(JSON.stringify(data));

        if (type == ReportComponentType.IMAGE) {
            let imageData = dataCopy as ImageComponentData;
            // report-scoped uploads must not follow a template out of their report; ORG_ASSET is org-wide and survives
            let unallowedTypes = [ReportDataContext.SAMPLE_IMAGE, ReportDataContext.REPORT_ASSET];
            let shouldClear = unallowedTypes.includes(imageData.context);

            imageData = {
                ...imageData,
                dataKey: shouldClear ? null : imageData.dataKey,
                fileRef: shouldClear ? null : imageData.fileRef,
                // clear the whole triple — a stale context beside a null dataKey reads as "has data" downstream
                context: shouldClear ? ReportDataContext.UNKNOWN : imageData.context,
            }

            dataCopy = imageData;
        }

        return dataCopy;
    }

}

// TODO FIND A BETTER PLACE FOR THIS FUNCTION
export const editModeCheck = (editMode: boolean) => {
    if (!editMode) {
        console.warn('[AdvancedReportState] Action Blocked While In Preview Mode');
        return false
    }
    return true
}